import * as React from 'react';
import {connect} from 'react-redux';

import _ from 'underscore';

import {Assignables} from 'nutshell-core/assignables';
import type {Assignable} from 'nutshell-core/types';
import {stripTypename} from 'nutshell-core/utils/strip-typename';

import {AssigneeSelect} from 'shells/assignee-select/assignee-select';
import {CrossBrowserHiddenFormSubmit} from 'shells/form/cross-browser-hidden-form-submit';
import {Label} from 'shells/form/label';
import {TextField} from 'shells/textfield/textfield';

import type {DueDateUnit} from 'nutshell-graphql-types';
import type {ClosedLeadTaskTemplateDueDateInput} from 'nutshell-graphql-types';

import {SalesAutomationSidebarFormWrapper} from '../../../core/sidebar/sales-automation-sidebar-form-wrapper';

import type {ClosedLeadTaskTemplateItem} from '../../../../sales-automation/types';
import type {NutshellState} from '../../../../store';

import {DateTimePicker} from './date-time-picker';

import styles from '../sales-process-form.module.css';

function mapStateToProps(state: NutshellState): StateProps {
    return {
        // @ts-expect-error
        assignables: Assignables.getAllEnabled(state),
    };
}

export type TaskTemplateInput = {
    name: string;
    description?: string | null | undefined;
    dueDate?:
        | {
              dueUnit: DueDateUnit;
              dueTimespan: number;
          }
        | null
        | undefined;
    assigneeId?: string | null | undefined;
};

type OwnProps = {
    entityId: string | null | undefined;
    taskTemplate?: ClosedLeadTaskTemplateItem | null | undefined;
    formSubmitBtnRef: (arg1?: HTMLInputElement | null | undefined) => void;
    onSubmit: (arg1: TaskTemplateInput) => Promise<any> | null | undefined;
    onDelete: (arg1: {id: string}) => Promise<any>;
};

type StateProps = {
    assignables: Assignable[];
};

type State = {
    name: string;
    description: string;
    assigneeId: string | null | undefined;
    dueDate: ClosedLeadTaskTemplateDueDateInput | null | undefined;
};

type Props = OwnProps & StateProps;

export class ClosedLeadTaskTemplateFormComponent extends React.PureComponent<Props, State> {
    nameTextFieldRef: HTMLInputElement | null | undefined;

    constructor(props: Props) {
        super(props);

        const taskTemplate = props.taskTemplate;

        this.state = {
            name: taskTemplate ? taskTemplate.name : '',
            description: taskTemplate && taskTemplate.description ? taskTemplate.description : '',
            assigneeId: taskTemplate && taskTemplate.assignee ? taskTemplate.assignee.id : null,
            dueDate: taskTemplate ? stripTypename(taskTemplate.dueDate) : null,
        };
    }

    componentDidMount() {
        // Using defer to ensure text is filled before it is selected
        // Without this, text will not be selected properly on first mount
        _.defer(() => {
            if (this.nameTextFieldRef) {
                this.nameTextFieldRef.focus();
            }
        });
    }

    render() {
        return (
            <SalesAutomationSidebarFormWrapper
                id={this.props.entityId}
                onSaveForm={this.saveForm}
                onDelete={{
                    deleteText: 'Remove task',
                    deleteConfirmationTitle: 'Are you sure you want to remove this task?',
                    onDeleteConfirm: this.handleDeleteConfirm,
                }}
            >
                <div className={styles['form-input']}>
                    <Label required={true} htmlFor='TitleTextField'>
                        Title
                    </Label>
                    <TextField
                        // @ts-expect-error
                        name='taskName'
                        id='TitleTextField'
                        required={true}
                        placeholder='Example: Prepare demo materials'
                        textFieldRef={(c) => {
                            this.nameTextFieldRef = c;
                        }}
                        onFocus={this.handleFocus}
                        value={this.state.name}
                        onChange={this.handleNameChange}
                    />
                </div>
                <div className={styles['form-input']}>
                    <Label htmlFor='AssigneePicker'>Assignee</Label>
                    <AssigneeSelect
                        inputId='AssigneePicker'
                        placeholder='Assigned to lead owner'
                        onChange={this.handleAssigneeChange}
                        value={this.state.assigneeId}
                        options={this.props.assignables}
                    />
                </div>
                <div className={styles['form-input']}>
                    <DateTimePicker
                        dueDate={this.state.dueDate}
                        onDueDateChange={this.handleDueDateChange}
                    />
                </div>
                <div className={styles['form-input']}>
                    <Label htmlFor='DescriptionTextField'>Description</Label>
                    <TextField
                        // @ts-expect-error
                        name='description'
                        id='DescriptionTextField'
                        placeholder='Add a note or helpful sales coaching tips'
                        value={this.state.description}
                        multiline={true}
                        onChange={this.handleDescriptionChange}
                    />
                </div>
                <CrossBrowserHiddenFormSubmit inputRef={this.props.formSubmitBtnRef} />
            </SalesAutomationSidebarFormWrapper>
        );
    }

    handleDueDateChange = (newValue?: ClosedLeadTaskTemplateDueDateInput | null) => {
        this.setState({
            dueDate: newValue,
        });
    };

    // This is for selecting the entire input field
    handleFocus = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.target.select();
    };

    handleNameChange = (val: string) => {
        this.setState({name: val});
    };

    handleDescriptionChange = (val: string) => {
        this.setState({description: val});
    };

    handleAssigneeChange = (assigneeId?: string | null) => {
        this.setState({assigneeId});
    };

    handleDeleteConfirm = (entityId: string) => {
        return this.props.onDelete({
            id: entityId,
        });
    };

    saveForm = () => {
        return this.props.onSubmit({
            name: this.state.name,
            description: this.state.description || '',
            assigneeId: this.state.assigneeId,
            dueDate: this.state.dueDate,
        });
    };
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps);
export const ClosedLeadTaskTemplateForm = connector(ClosedLeadTaskTemplateFormComponent);
